<template>
  <div class="multiple-file-uploader">
    <!-- Drag and Drop Box -->
    <div
      ref="dragDropBox"
      class="multiple-file-uploader-box flex-center-vertically text-center"
      :class="{ uploading: isUploading }"
      style="min-height: 150px; height: auto; border: 1px dashed"
      :style="{
        borderColor: activeColor,
        cursor: readonly ? '' : 'pointer',
        backgroundColor: readonly ? secondary : '',
        width: fullWidth ? '' : `${pw}px`,
        height: fullWidth ? '' : `${ph}px`
      }"
      @click="chooseFile"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <!-- Show loading spinner when uploading -->
      <div v-if="isUploading" class="upload-loading">
        <b-spinner variant="primary" />
        <div class="mt-1">{{ $t('general.uploading') }}</div>
      </div>

      <!-- Show icon & hint if no files and not uploading -->
      <div v-else>
        <feather-icon
          icon="FileIcon"
          size="30"
          :style="{ color: readonly ? '#ffffff' : activeColor }"
        />
        <div
          class="mt-2"
          :style="{ color: readonly ? '#ffffff' : activeColor }"
        >
          {{ $t('general.clickOrDragDropToUploadFile') }}
        </div>
      </div>
    </div>

    <!-- File List -->
    <div v-if="fileList.length" class="file-list mt-1">
      <div
        v-for="(fileItem, index) in visibleFiles"
        :key="index"
        class="file-item d-flex align-items-center"
      >
        <!-- File preview section (both images and other files) -->
        <div
          class="file-preview cursor-pointer"
          @click="showImagePreview(fileItem)"
        >
          <img
            v-if="isImageFile(fileItem.file.type)"
            :src="getFilePreview(fileItem)"
            alt="preview"
          />
          <feather-icon
            v-else
            :icon="getFileIcon(fileItem.file.type)"
            size="20"
          />
        </div>
        <span
          class="ml-2 file-name cursor-pointer"
          @click="showImagePreview(fileItem)"
        >
          {{ fileItem.file.name }}
        </span>
        <button
          class="remove-button ml-auto"
          @click.prevent.stop="remove(index)"
        >
          <CloseIcon :width="20" :height="20" />
        </button>
      </div>

      <!-- Show More Button -->
      <div
        v-if="fileList.length > 5 && !showAll"
        class="show-more-button mt-2 cursor-pointer"
        @click="showAll = true"
      >
        {{ $t('general.showAllFiles', { count: fileList.length - 5 }) }}
      </div>
    </div>

    <!-- Hidden File Input -->
    <input
      ref="fileInput"
      type="file"
      style="display: none"
      multiple
      @change="onChange"
    />

    <!-- Image Modal -->
    <b-modal
      v-model="isModalVisible"
      size="lg"
      centered
      hide-footer
      class="image-modal"
      body-class="modal-img-content"
      @hidden="resetModal"
    >
      <div class="modal-image-container">
        <div
          v-if="fileList.length > 1"
          class="nav-button prev-button"
          :class="{ disabled: currentImageIndex === 0 }"
          @click="prevImage"
        >
          <feather-icon size="32" icon="ChevronLeftIcon" />
        </div>
        <div class="image-wrapper">
          <img
            v-if="currentImage.file && isImageFile(currentImage.file.type)"
            :src="currentImage.preview"
            class="preview-one"
            alt="Full Image"
          />
          <video
            v-else-if="currentImage.file && isVideoFile(currentImage.file.type)"
            :src="currentImage.preview"
            class="preview-one"
            controls
          />
          <div v-else class="preview-one file-preview-fallback">
            <feather-icon
              :icon="
                getFileIcon(currentImage.file ? currentImage.file.type : '')
              "
              size="128"
            />
            <div class="file-name mt-2">
              {{ currentImage.file ? currentImage.file.name : '' }}
            </div>
          </div>
        </div>
        <div
          v-if="fileList.length > 1"
          class="nav-button next-button"
          :class="{ disabled: currentImageIndex === fileList.length - 1 }"
          @click="nextImage"
        >
          <feather-icon size="32" icon="ChevronRightIcon" />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import CloseIcon from '@/components/icons/CloseIcon.vue';
  import { BSpinner, BModal, BButton } from 'bootstrap-vue';
  import { $themeConfig } from '@themeConfig';
  import Repository from '@/repositories/RepositoryFactory';
  import Swal from 'sweetalert2'

  const FileRepository = Repository.get('file');

  export default {
    name: 'NMultipleFileUploader',
    components: {
      CloseIcon,
      BSpinner,
      BModal,
      BButton,
    },

    props: {
      value: {
        type: Array,
        default: () => []
      },
      pw: {
        type: Number,
        default: 400
      },
      ph: {
        type: Number,
        default: 150
      },
      fullWidth: {
        type: Boolean,
        default: true
      },
      readonly: {
        type: Boolean,
        default: false
      },
      allowClipboard: {
        type: Boolean,
        default: true
      }
    },

    setup() {
      const { primary, secondary } = $themeConfig.color;
      const activeColor = secondary;
      return { primary, secondary, activeColor };
    },

    data() {
      return {
        fileList: [],
        isUploading: false,
        showAll: false,
        isModalVisible: false,
        currentImageIndex: 0
      };
    },

    computed: {
      visibleFiles() {
        return this.showAll ? this.fileList : this.fileList.slice(0, 5);
      },
      currentImage() {
        const image = this.fileList[this.currentImageIndex];
        return (
          image || {
            file: { type: '', name: '' },
            preview: null
          }
        );
      }
    },

    methods: {
      addPasteListener() {
        if (this._hasPasteListener) return;
        console.log('addPasteListener');
        this._onPasteBound = this.onPaste.bind(this);
        window.addEventListener('paste', this._onPasteBound);
        this._hasPasteListener = true;
      },

      removePasteListener() {
        if (!this._hasPasteListener) return;
        console.log('removePasteListener');
        window.removeEventListener('paste', this._onPasteBound);
        this._hasPasteListener = false;
        this._onPasteBound = null;
      },

      async onPaste(event) {
        if (this.readonly) return;
        if (!this.allowClipboard) return;
        const clipboardData = event.clipboardData || window.clipboardData;
        
        if (!clipboardData) return;

        let incomingFiles = [];

        if (clipboardData.items && clipboardData.items.length) {
          for (const item of clipboardData.items) {
            if (item.kind === 'file') {
              const file = item.getAsFile();
              if (file) incomingFiles.push(file);
            }
          }
        }

        if (!incomingFiles.length && clipboardData.files && clipboardData.files.length) {
          incomingFiles = [...clipboardData.files];
        }

        if (!incomingFiles.length) return;

        // Prevent default paste into any focused field when we actually paste files
        event.preventDefault();

        for (const file of incomingFiles) {
          const result = await this.uploadFile(file);
          if (result) {
            this.fileList.push(result);
            this.emitFiles();
          }
        }
      },
      async uploadFile(file) {
        this.isUploading = true;
        this.$emit('uploading', true);
        try {
          const formData = new FormData();
          formData.append('image', file);
          const response = await FileRepository.upload(formData);
          
          return {
            file,
            preview: (this.isImageFile(file.type) || this.isVideoFile(file.type))
              ? URL.createObjectURL(file)
              : null,
            mediaId: response?.data?.data?.mediaId,
            originalName: file.name,
            type: this.isImageFile(file.type) ? 'photo' : (this.isVideoFile(file.type) ? 'video' : 'file'),
            mimeType: file.type,
            url: response?.data?.data?.url,
          };
        } catch (error) {
          Swal.fire({
            title: this.$t('general.warning'),
            icon: 'info',
            text: this.$t('error.fileSize'),
            customClass: {
              confirmButton: 'btn btn-primary'
            },
            confirmButtonText: this.$t('button.close'),
            buttonsStyling: false
          });
          return null;
        } finally {
          this.isUploading = false;
          this.$emit('uploading', false);
        }
      },

      async onChange() {
        if (this.readonly) return;

        const selectedFiles = [...this.$refs.fileInput.files];

        for (const file of selectedFiles) {
          const result = await this.uploadFile(file);
          if (result) {
            this.fileList.push(result);
            this.emitFiles();
          }
        }
      },

      emitFiles() {
        const files = this.fileList.map(item => item.file);
        const mediaIds = this.fileList
          .map(item => item.mediaId)
          .filter(id => id);

        this.$emit('input', files);
        this.$emit('change', files);
        this.$emit('media-ids', mediaIds);
        this.$emit('files', this.fileList);
      },

      async drop(event) {
        if (this.readonly) return;
        event.preventDefault();

        const droppedFiles = [...event.dataTransfer.files];

        for (const file of droppedFiles) {
          const result = await this.uploadFile(file);
          if (result) {
            this.$refs.fileInput.value = null;
            this.fileList.push(result);
            this.emitFiles();
          }
        }
      },

      remove(index) {
        if (this.fileList[index].preview) {
          URL.revokeObjectURL(this.fileList[index].preview);
        }
        this.fileList.splice(index, 1);
        this.emitFiles();
      },

      chooseFile() {
        if (this.readonly) return;
        this.$refs.fileInput.value = null;
        this.$refs.fileInput.click();
      },

      dragover(event) {
        if (this.readonly) return;
        event.preventDefault();
        this.activeColor = this.primary;
      },

      dragleave(event) {
        if (this.readonly) return;
        this.activeColor = this.secondary;
      },

      getFileIcon(mimeType) {
        // Return appropriate icon based on file type
        if (mimeType.startsWith('image/')) return 'ImageIcon';
        if (mimeType.startsWith('video/')) return 'VideoIcon';
        if (mimeType.startsWith('audio/')) return 'MusicIcon';
        if (mimeType.includes('pdf')) return 'FileTextIcon';
        if (mimeType.includes('spreadsheet') || mimeType.includes('excel'))
          return 'FileIcon';
        if (mimeType.includes('document') || mimeType.includes('word'))
          return 'FileTextIcon';
        return 'FileIcon';
      },

      isImageFile(mimeType) {
        return mimeType.startsWith('image/');
      },

      isVideoFile(mimeType) {
        return mimeType.startsWith('video/');
      },

      getFilePreview(fileItem) {
        return fileItem.preview || null;
      },

      showImagePreview(fileItem) {
        const index = this.fileList.findIndex(item => item === fileItem);
        if (index !== -1) {
          this.currentImageIndex = index;
          this.isModalVisible = true;
        }
      },

      prevImage() {
        if (this.currentImageIndex > 0) {
          this.currentImageIndex--;
        }
      },

      nextImage() {
        if (this.currentImageIndex < this.fileList.length - 1) {
          this.currentImageIndex++;
        }
      },

      resetModal() {
        this.currentImageIndex = 0;
      }
    },

    watch: {
      allowClipboard(val) {
        if (val) this.addPasteListener();
        else this.removePasteListener();
      }
    },

    mounted() {
      if (this.allowClipboard) {
        this.addPasteListener();
      }
    },

    // Clean up URLs when component is destroyed
    beforeDestroy() {
      this.removePasteListener();
      this.fileList.forEach(fileItem => {
        if (this.isImageFile(fileItem.file.type) || this.isVideoFile(fileItem.file.type)) {
          URL.revokeObjectURL(this.getFilePreview(fileItem));
        }
      });
    }
  };
</script>

<style scoped>
  .flex-center-vertically {
    display: flex;
    justify-content: center;
    flex-direction: column;
  }

  .file-item {
    border: 1px solid #eee;
    border-radius: 4px;
    margin-bottom: 8px;
    align-items: center;
    padding: 4px 8px;
  }

  .file-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-left: 8px;
    flex: 1;
  }

  .remove-button {
    background: none;
    border: none;
    padding: 2px;
    cursor: pointer;
    margin-left: 8px;
  }

  .upload-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .uploading {
    opacity: 0.7;
    pointer-events: none;
  }

  .file-preview {
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f8f9fa;
    transition: transform 0.2s ease;
  }

  .file-preview:hover {
    transform: scale(1.05);
  }

  .file-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .image-modal {
    max-width: 600px !important;
    width: 40vw !important;
    margin: 1.75rem auto;
  }

  .modal-image-container {
    position: relative;
    width: 100%;
    height: 50vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
    padding: 20px;
  }

  .image-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 60px;
  }

  .preview-one {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 4px;
  }

  .nav-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .nav-button:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-50%) scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .nav-button.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  .prev-button {
    left: 10px;
  }

  .next-button {
    right: 10px;
  }

  .file-preview-fallback {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  .file-preview-fallback .file-name {
    margin-top: 1rem;
    font-size: 1.2rem;
    color: #6e6b7b;
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .file-name:hover {
    color: #7367f0;
    text-decoration: underline;
  }
</style>

<style>
  .modal-img-content {
    padding: 0 !important;
    height: 50vh !important;
  }

  .image-modal {
    width: 400px !important;
  }

  .image-modal .modal-dialog {
    width: 400px !important;
    max-width: 400px !important;
    margin: 1.75rem auto !important;
  }

  .image-modal .modal-content {
    width: 400px !important;
    height: 50vh !important;
  }

  .image-modal .modal-body {
    width: 400px !important;
    padding: 0 !important;
    height: 50vh !important;
  }
</style>
